import { Autocomplete, Button, FormControl, InputLabel, OutlinedInput, Stack, TextField } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { IngestConfigPackColumnSet } from "../../../Core/Api/Types/ingest-config-pack-column-set";
import { cacheService, ingestService } from "../../../Core/Api/services";
import { produce } from "immer";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { useTranslation } from "react-i18next";
import { AddCircle, RemoveCircle } from "@mui/icons-material";

export type ModalEditColumnElementProps = {
  column: IngestConfigPackColumnSet;
};

export type ModalEditColumnProps = CommonModalProps<ModalEditColumnElementProps>;

const ModalEditColumn = React.forwardRef<any, ModalEditColumnProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...modalProps } = props;
  const enqueueSnackbar = useCustomSnackbar();
  const { column } = modalProps;
  const [selectedColumn, setSelectedColumn] = React.useState<IngestConfigPackColumnSet>(column);
  const metas: DbMetadataFieldInfos[] = cacheService.GetMetadata().filter((f) => f.isSearchable && f.layer === "Asset");

  const handleConfirmClick = () => {
    ingestService.UpdateIngestConfigColumnSet(selectedColumn.columnInfos!).then(() => {
      enqueueSnackbar(t("alert:success.columnUpdated"), "success");
    });
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.modifyColumn")}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1}>
        <FormControl sx={{ width: "100%", mb: 0.5 }}>
          <InputLabel shrink>{t("field:label.orderPosition")}</InputLabel>
          <OutlinedInput
            type="number"
            value={selectedColumn.columnInfos?.order}
            onChange={(e) =>
              setSelectedColumn(
                produce((draft) => {
                  draft.columnInfos!.order = parseInt(e.target.value);
                })
              )
            }
          />
        </FormControl>

        {selectedColumn.columnInfos?.dependencies?.map((d, i) => (
          <>
            {" "}
            <FormControl sx={{ width: "100%", mb: 0.5 }}>
              <InputLabel shrink>{t("field:label.activeForFieldValues")}</InputLabel>
              <Autocomplete
                value={d.activeForValues ?? []}
                onChange={(e, value) => {
                  setSelectedColumn(
                    produce((draft) => {
                      if (!draft.columnInfos?.dependencies) return;
                      draft.columnInfos!.dependencies[i].activeForValues = value;
                      draft.columnInfos = { ...draft.columnInfos };
                    })
                  );
                }}
                options={selectedColumn.columnFieldInfos?.values?.filter((x) => x.value).map((x) => x.value!) ?? []}
                multiple
                freeSolo
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>
            <FormControl sx={{ width: "100%", mb: 0.5 }}>
              <InputLabel shrink>{t("field:label.dependencyMetadataField")}</InputLabel>
              <Autocomplete
                value={metas.find((x) => x.id === d.fieldId) || null}
                onChange={(e, value) =>
                  setSelectedColumn(
                    produce((draft) => {
                      if (!draft.columnInfos?.dependencies) return;
                      if (value !== null) {
                        draft.columnInfos!.dependencies[i].fieldId = value.id;
                      } else {
                        draft.columnInfos!.dependencies[i].fieldId = null;
                        draft.columnInfos!.dependencies[i].values = [];
                      }
                      draft.columnInfos = { ...draft.columnInfos };
                    })
                  )
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={metas}
                sx={{ width: "100%" }}
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>
            <FormControl sx={{ width: "100%", mb: 0.5 }}>
              <InputLabel shrink>{t("field:label.metadataFieldReferenceValues")}</InputLabel>
              <Autocomplete
                value={d.values ?? []}
                onChange={(e, value) => {
                  setSelectedColumn(
                    produce((draft) => {
                      if (!draft.columnInfos?.dependencies) return;
                      draft.columnInfos!.dependencies[i].values = value;
                      draft.columnInfos = { ...draft.columnInfos };
                    })
                  );
                }}
                options={metas.find((x) => x.id === d.fieldId)?.values?.map((x) => x.value ?? "") ?? []}
                multiple
                freeSolo
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>
            {selectedColumn.columnInfos!.dependencies!.length > 1 && (
              <Button
                onClick={(e) => {
                  setSelectedColumn(
                    produce((draft) => {
                      if (!draft.columnInfos) return;
                      draft.columnInfos.dependencies! = draft.columnInfos.dependencies!.filter((x, n) => i !== n);
                      draft.columnInfos = { ...draft.columnInfos };
                    })
                  );
                }}
                color="error"
                endIcon={<RemoveCircle />}
              >
                {t("button:removeDependency")}
              </Button>
            )}
          </>
        ))}
        <Button
          onClick={() => {
            setSelectedColumn(
              produce((draft) => {
                if (!draft.columnInfos) return;
                draft.columnInfos.dependencies! = [
                  ...draft.columnInfos.dependencies!,
                  { fieldId: null, activeForValues: null, values: null },
                ];
                draft.columnInfos = { ...draft.columnInfos };
              })
            );
          }}
          endIcon={<AddCircle />}
        >
          {t("button:addDependency")}
        </Button>
      </Stack>
    </Modal>
  );
});

export default ModalEditColumn;
